<template>
  <div class="ai-generate-mode-panel ai-generate-fusion">
    <div class="ai-gen-section">
      <div class="ai-gen-section-title">
        全局通用提示词 (Prompt)
        <span class="ai-gen-hint">（非必填，任务提示词为空时使用）</span>
      </div>
      <Input v-model="globalPrompt" type="textarea" :rows="4" :disabled="generating"
        placeholder="例如：将台灯放在现代简约风格的床头柜上，柔和暖光，大理石背景，超高清..." @input="handleGlobalPromptInput" />
    </div>

    <div class="ai-gen-section">
      <div class="ai-gen-section-head">
        <div class="ai-gen-section-title">
          公共参考图片
          <span class="ai-gen-hint">（最多 5 张，不支持拖动）</span>
        </div>
        <div class="ai-gen-ref-summary">已选 {{ selectedPublicReferences.length }} / {{ publicReferences.length }} 张</div>
      </div>

      <div class="ai-fusion-ref-box">
        <Spin v-if="uploadQueues.public.length > 0 || uploadingCounts.public > 0" fix>
          <div role="status" aria-live="polite">正在上传中...</div>
        </Spin>
        <div class="ai-fusion-ref-list">
          <div v-for="(item, index) in publicReferences" :key="item.refKey"
            :class="['ai-fusion-ref-item', { selected: item.selected }]"
            :title="item.selected ? '已选用，点击取消' : '未选用，点击选中'" @click="togglePublicReference(item)">
            <div class="ai-fusion-ref-thumb">
              <img :src="filenodeViewTargetUrl + item.path" />
              <div class="ai-fusion-image-badges">
                <span class="ai-fusion-image-badge is-order">{{ index + 1 }}</span>
                <span v-if="item.sourceType === 'upload'" class="ai-fusion-image-badge is-source">本地</span>
                <template v-else>
                  <span class="ai-fusion-image-badge is-lang">{{ getLangLabel(item.language) }}</span>
                  <span v-if="dimension === 'sku' && item.setName" class="ai-fusion-image-badge is-set"
                    :style="{ background: getSetTypeBadgeColor(item.setType) }">
                    {{ item.setName }}
                  </span>
                </template>
              </div>
              <span class="ai-fusion-ref-check">{{ item.selected ? "✓" : "" }}</span>
              <div class="ai-fusion-image-cover">
                <Icon type="ios-trash-outline" title="移除" @click.native.stop="removePublicReference(index)" />
              </div>
            </div>
          </div>

          <dytUpload v-if="!generating && publicReferences.length < 5" ref="publicUploadRef"
            class="ai-gen-upload no-drag" file-name="files" :headers="uploadFilesHeader" :max-size="uploadFilesMaxSize"
            :on-exceeded-size="handleExceedSize" :show-upload-list="false" :format="['jpg', 'png', 'gif']"
            accept="image/*" :on-success="handleUploadSuccess" :on-error="handleUploadError"
            :on-format-error="handleFormatError" multiple :before-upload="(file) => beforeUpload(file, 'public')"
            :action="uploadFilesUrl">
            <div class="ai-gen-add-item">
              <Icon type="ios-folder-outline" size="38" color="#bbb" />
              <div>上传本地图片</div>
              <span>支持多选文件</span>
            </div>
          </dytUpload>

          <div v-if="!generating && publicReferences.length < 5" class="ai-gen-add-item"
            @click="openSelectImageModal('public')">
            <Icon type="ios-images-outline" size="38" color="#bbb" />
            <div>选用已有图案</div>
            <span>从图库中批量选择</span>
          </div>
        </div>
      </div>
    </div>

    <div class="ai-gen-section">
      <div class="ai-gen-section-head">
        <div class="ai-gen-section-title">
          商品主图图片与生成模式
          <span class="ai-gen-hint">（支持拖拽调整排序；主图顺序对应下方任务顺序）</span>
        </div>
        <div v-if="mainImages.length > 0" class="ai-gen-ref-summary">已选 {{ mainImages.length }} 张（{{ aiMainImages.length
        }}
          张AI处理，{{ keepMainImages.length }} 张保留原图）</div>
      </div>

      <div class="ai-fusion-main-box">
        <Spin v-if="uploadQueues.main.length > 0 || uploadingCounts.main > 0" fix>
          <div role="status" aria-live="polite">正在上传中...</div>
        </Spin>
        <draggable v-model="mainImages" :disabled="generating" :animation="200" ghost-class="ai-gen-drag-ghost"
          chosen-class="ai-gen-drag-chosen" drag-class="ai-gen-dragging" class="ai-fusion-main-list"
          :filter="'.no-drag'" :prevent-on-filter="false">
          <div v-for="(item, index) in mainImages" :key="getImageKey(item)" class="ai-fusion-main-item">
            <div class="ai-fusion-main-thumb">
              <img :src="filenodeViewTargetUrl + item.path" />
              <div class="ai-fusion-image-badges">
                <span class="ai-fusion-image-badge is-order">{{ index + 1 }}</span>
                <span v-if="item.sourceType === 'upload'" class="ai-fusion-image-badge is-source">本地</span>
                <template v-else>
                  <span class="ai-fusion-image-badge is-lang">{{ getLangLabel(item.language) }}</span>
                  <span v-if="dimension === 'sku' && item.setName" class="ai-fusion-image-badge is-set"
                    :style="{ background: getSetTypeBadgeColor(item.setType) }">
                    {{ item.setName }}
                  </span>
                </template>
              </div>
              <div class="ai-fusion-image-cover">
                <Icon type="ios-trash-outline" title="移除" @click.native.stop="removeMainImage(index)" />
              </div>
            </div>
            <div class="ai-fusion-mode-btns">
              <button type="button" :class="{ active: getGenMode(item) === 'ai' }" :disabled="generating"
                @click="setGenMode(item, 'ai')">AI生图</button>
              <button type="button" :class="{ active: getGenMode(item) === 'keep' }" :disabled="generating"
                @click="setGenMode(item, 'keep')">保留原图</button>
            </div>
          </div>

          <dytUpload v-if="!generating" ref="mainUploadRef" class="ai-gen-upload no-drag" file-name="files"
            :headers="uploadFilesHeader" :max-size="uploadFilesMaxSize" :on-exceeded-size="handleExceedSize"
            :show-upload-list="false" :format="['jpg', 'png', 'gif']" accept="image/*" :on-success="handleUploadSuccess"
            :on-error="handleUploadError" :on-format-error="handleFormatError" multiple
            :before-upload="(file) => beforeUpload(file, 'main')" :action="uploadFilesUrl">
            <div class="ai-gen-add-item">
              <Icon type="ios-folder-outline" size="38" color="#bbb" />
              <div>上传本地图片</div>
              <span>支持多选文件</span>
            </div>
          </dytUpload>

          <div v-if="!generating" class="ai-gen-add-item no-drag" @click="openSelectImageModal('main')">
            <Icon type="ios-images-outline" size="38" color="#bbb" />
            <div>选用已有图案</div>
            <span>从图库中批量选择</span>
          </div>
        </draggable>
      </div>
    </div>

    <div class="ai-gen-section">
      <div class="ai-gen-section-head ai-fusion-task-head">
        <div class="ai-gen-section-title">生成任务明细清单</div>
        <div class="ai-gen-hint">提示词默认使用全局提示词，支持任务独立提示词</div>
      </div>

      <div class="ai-fusion-task-list">
        <div v-if="generationTaskImages.length === 0" class="ai-fusion-empty">暂无可生成任务，请选用公共参考图片，并添加“AI生图”模式的商品主图</div>

        <div v-for="(main, index) in generationTaskImages" :key="`task-${getImageKey(main)}`" class="ai-fusion-task">
          <div class="ai-fusion-task-title">任务 {{ index + 1 }}</div>

          <div class="ai-fusion-task-images">
            <div class="ai-fusion-task-image">
              <div class="ai-fusion-task-thumb">
                <img :src="filenodeViewTargetUrl + main.path" />
              </div>
              <span>图1</span>
            </div>

            <template v-if="getGenMode(main) === 'ai'">
              <span class="ai-fusion-plus">+</span>
              <div class="ai-fusion-task-references">
                <span class="ai-fusion-reference-label">参考图：</span>
                <div class="ai-fusion-task-reference-list">
                  <div v-for="reference in selectedPublicReferences" :key="`${getImageKey(main)}-${reference.refKey}`"
                    class="ai-fusion-task-reference">
                    <button type="button" :class="{ selected: isTaskReferenceSelected(main, reference) }"
                      :disabled="generating" @click="toggleTaskReference(main, reference)">
                      <span>{{ isTaskReferenceSelected(main, reference) ? "✓" : "" }}</span>
                      <img :src="filenodeViewTargetUrl + reference.path" />
                    </button>
                    <!-- 主图固定为图1；仅为当前任务勾选的参考图按排列顺序连续编号。 -->
                    <small v-if="isTaskReferenceSelected(main, reference)">图{{getTaskReferences(main).findIndex((item) =>
                      item.refKey === reference.refKey) + 2}}</small>
                  </div>
                </div>
              </div>
            </template>

            <div v-else class="ai-fusion-keep-note">保留原图，不进行 AI 参考处理</div>
          </div>

          <div class="ai-fusion-task-prompt">
            <div class="ai-fusion-task-prompt-label">
              提示词 (Prompt)
              <span v-if="getGenMode(main) === 'ai'">*</span>
            </div>
            <Input type="textarea" :rows="3" :value="main.prompt" :disabled="generating || getGenMode(main) === 'keep'"
              :placeholder="getGenMode(main) === 'keep' ? '保留原图无需输入提示词' : '任务提示词为空时使用全局通用提示词'"
              @input="handleTaskPromptInput(main, $event)" />
          </div>
        </div>
      </div>
    </div>

    <aiSelectImageModal v-model="selectImageModalVisible" :dimension="dimension"
      :multi-lang-image-data="multiLangImageData" :sku-image-set-list="skuImageSetList"
      :pre-selected="existingImagesPreSelected" :filenode-view-target-url="filenodeViewTargetUrl"
      @on-ok="handleSelectImageOk" />
  </div>
</template>

<script>
import api from "@/api/api";
import aiSelectImageModal from "./aiSelectImageModal";
import draggable from "vuedraggable";

export default {
  name: "aiGenerateFusionModal",
  components: {
    aiSelectImageModal,
    draggable,
  },
  props: {
    dimension: {
      type: String,
      default: "spu",
    },
    multiLangImageData: {
      type: Array,
      default: () => [],
    },
    skuImageSetList: {
      type: Array,
      default: () => [],
    },
    filenodeViewTargetUrl: {
      type: String,
      default: "",
    },
    generating: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      globalPrompt: "",
      // 公共池保存图片及全局勾选状态，主图通过 referenceKeys 保存各自选用的参考图。
      publicReferences: [],
      mainImages: [],
      selectImageModalVisible: false,
      selectTarget: "", // public：公共参考图；main：商品主图，共用图库选择弹窗。
      uploadFilesUrl: api.upload_files + "?basePath=/product",
      uploadFilesMaxSize: 10240,
      uploadMaxLength: 20,
      // 两类上传分别排队和计时，避免同时选择文件时相互影响。
      uploadQueues: {
        public: [],
        main: [],
      },
      uploadingCounts: {
        public: 0,
        main: 0,
      },
      uploadTimers: {
        public: null,
        main: null,
      },
      uploadVersion: 0, // 表单重置后递增，用于忽略上一轮上传请求的返回结果。
    };
  },
  computed: {
    uploadFilesHeader() {
      return {
        ...this.$store.getters.erpRequestHeaders,
        ...this.$store.getters.dytRequestHeaders,
      };
    },
    existingImagesPreSelected() {
      // 图库只回显已有图库图片，本地上传图片由当前组件保留。
      const list = this.selectTarget === "public" ? this.publicReferences : this.mainImages;
      return list.filter((item) => item.sourceType !== "upload");
    },
    selectedPublicReferences() {
      // 未显式取消的图片默认选中，过滤后仍保留公共池中的排列顺序。
      return this.publicReferences.filter((item) => item.selected !== false);
    },
    aiMainImages() {
      return this.mainImages.filter((item) => this.getGenMode(item) === "ai");
    },
    generationTaskImages() {
      // 有可用公共参考图时才展示 AI 任务，任务顺序跟随主图拖拽顺序。
      return this.selectedPublicReferences.length > 0 ? this.aiMainImages : [];
    },
    keepMainImages() {
      return this.mainImages.filter((item) => this.getGenMode(item) === "keep");
    },
  },
  methods: {
    resetForm() {
      // 清除尚未触发的上传；已发出的请求由版本号阻止回填。
      this.uploadVersion += 1;
      Object.keys(this.uploadTimers).forEach((key) => {
        if (this.uploadTimers[key]) clearTimeout(this.uploadTimers[key]);
      });
      this.globalPrompt = "";
      this.publicReferences = [];
      this.mainImages = [];
      this.selectImageModalVisible = false;
      this.selectTarget = "";
      this.uploadQueues = { public: [], main: [] };
      this.uploadingCounts = { public: 0, main: 0 };
    },
    getImageKey(item) {
      // 图库图片优先使用 ID；无 ID 时组合维度、图集、语言和路径标识图片。
      if (item.imageId !== undefined && item.imageId !== null && item.imageId !== "") {
        return `${this.dimension}-id-${item.imageId}`;
      }
      return `${this.dimension}-${item.productGoodsImageSetId || ""}-${item.language || "common"}-${item.path}`;
    },
    getLangLabel(lang) {
      const map = {
        common: "通用",
        en: "EN",
        fr: "FR",
        de: "DE",
        ru: "RU",
        nl: "NL",
      };
      return map[lang] || lang || "";
    },
    getSetTypeBadgeColor(setType) {
      const map = { 1: "#f90", 2: "#19be6b", 3: "#2d8cf0" };
      return map[String(setType)] || "#999";
    },
    withReferenceKey(item) {
      return {
        ...item,
        refKey: this.getImageKey(item),
      };
    },
    uniqueImages(list) {
      // 按图片标识去重，保留首次出现的图片及其排列位置。
      const result = [];
      const keys = [];
      list.forEach((item) => {
        const key = this.getImageKey(item);
        if (keys.indexOf(key) === -1) {
          keys.push(key);
          result.push(item);
        }
      });
      return result;
    },
    openSelectImageModal(target) {
      this.selectTarget = target;
      this.selectImageModalVisible = true;
    },
    handleSelectImageOk(selectedList) {
      if (this.selectTarget === "public") {
        this.updatePublicReferences(selectedList || []);
      } else {
        this.updateMainImages(selectedList || []);
      }
    },
    updatePublicReferences(selectedList) {
      // 图库重新选择时保留本地图片，并沿用已有图库图片的公共勾选状态。
      const oldList = this.publicReferences.slice();
      const oldMap = {};
      oldList.forEach((item) => {
        oldMap[this.getImageKey(item)] = item;
      });

      const uploadImages = oldList.filter((item) => item.sourceType === "upload");
      const libraryImages = selectedList.map((item) => {
        const key = this.getImageKey(item);
        const oldItem = oldMap[key];
        return this.withReferenceKey({
          ...item,
          selected: oldItem ? oldItem.selected !== false : true,
        });
      });
      let nextList = this.uniqueImages(uploadImages.concat(libraryImages));
      if (nextList.length > 5) {
        this.$Message.warning("公共参考图片最多选择5张");
        nextList = nextList.slice(0, 5);
      }

      const oldSelectedKeys = oldList.filter((item) => item.selected !== false).map((item) => this.getImageKey(item));
      const newSelectedKeys = nextList.filter((item) => item.selected !== false).map((item) => this.getImageKey(item));
      const addedKeys = newSelectedKeys.filter((key) => oldSelectedKeys.indexOf(key) === -1);

      this.publicReferences = nextList;
      // 清理失效引用并为所有主图勾选新增参考图，保留已有参考图的任务勾选状态。
      this.mainImages.forEach((main) => {
        let keys = (main.referenceKeys || []).filter((key) => newSelectedKeys.indexOf(key) !== -1);
        addedKeys.forEach((key) => {
          if (keys.indexOf(key) === -1) keys.push(key);
        });
        this.$set(main, "referenceKeys", keys);
      });
    },
    updateMainImages(selectedList) {
      // 本地主图继续保留，重新选中的图库主图复用原有任务配置。
      const oldMap = {};
      this.mainImages.forEach((item) => {
        oldMap[this.getImageKey(item)] = item;
      });
      const uploadImages = this.mainImages.filter((item) => item.sourceType === "upload");
      const libraryImages = selectedList.map((item) => {
        const key = this.getImageKey(item);
        return this.buildMainImage(item, oldMap[key]);
      });
      this.mainImages = this.uniqueImages(uploadImages.concat(libraryImages));
    },
    buildMainImage(item, oldItem) {
      // 新主图默认 AI 生图，继承全局提示词并选用全部已勾选的公共参考图。
      const data = this.withReferenceKey(item);
      return {
        ...data,
        genMode: oldItem ? this.getGenMode(oldItem) : "ai",
        prompt: oldItem ? oldItem.prompt : this.globalPrompt,
        isCustomPrompt: oldItem ? oldItem.isCustomPrompt : false,
        referenceKeys: oldItem && oldItem.referenceKeys ? oldItem.referenceKeys.slice() : this.selectedPublicReferences.map((reference) => reference.refKey),
      };
    },
    handleGlobalPromptInput(value) {
      // 仅同步未单独编辑过提示词的任务，避免覆盖任务自定义内容。
      this.globalPrompt = value;
      this.mainImages.forEach((item) => {
        if (!item.isCustomPrompt) this.$set(item, "prompt", value);
      });
    },
    handleTaskPromptInput(item, value) {
      // 手动编辑后停止跟随全局更新；为空时提交仍会回退到全局提示词。
      this.$set(item, "prompt", value);
      this.$set(item, "isCustomPrompt", true);
    },
    getGenMode(item) {
      return item.genMode === "keep" ? "keep" : "ai";
    },
    setGenMode(item, mode) {
      if (!this.generating) this.$set(item, "genMode", mode);
    },
    togglePublicReference(item) {
      // 公共勾选控制所有任务的可用参考图，重新勾选时同步加入所有主图。
      const wasSelected = item.selected !== false;
      this.$set(item, "selected", !wasSelected);
      this.mainImages.forEach((main) => {
        let keys = (main.referenceKeys || []).slice();
        if (wasSelected) {
          keys = keys.filter((key) => key !== item.refKey);
        } else if (keys.indexOf(item.refKey) === -1) {
          keys.push(item.refKey);
        }
        this.$set(main, "referenceKeys", keys);
      });
    },
    removePublicReference(index) {
      // 删除公共图片时同步清理各主图引用，避免残留无效的参考图标识。
      const item = this.publicReferences[index];
      this.publicReferences.splice(index, 1);
      if (!item) return;
      this.mainImages.forEach((main) => {
        this.$set(
          main,
          "referenceKeys",
          (main.referenceKeys || []).filter((key) => key !== item.refKey),
        );
      });
    },
    removeMainImage(index) {
      this.mainImages.splice(index, 1);
    },
    isTaskReferenceSelected(main, reference) {
      return (main.referenceKeys || []).indexOf(reference.refKey) !== -1;
    },
    toggleTaskReference(main, reference) {
      // 任务内的勾选只影响当前主图，不修改公共池或其他任务。
      const keys = (main.referenceKeys || []).slice();
      const index = keys.indexOf(reference.refKey);
      if (index === -1) keys.push(reference.refKey);
      else keys.splice(index, 1);
      this.$set(main, "referenceKeys", keys);
    },
    getEffectivePrompt(main) {
      return (main.prompt || "").trim() || (this.globalPrompt || "").trim();
    },
    imagePayload(item) {
      // 图库图片提交 imageId，无 ID 的上传图片提交 localImagePath。
      const data = {};
      if (item.imageId !== undefined && item.imageId !== null && item.imageId !== "") {
        data.imageId = item.imageId;
      } else {
        data.localImagePath = item.path;
      }
      return data;
    },
    getTaskReferences(main) {
      // 按公共池排列顺序取任务选中的图片，不采用 referenceKeys 的勾选先后顺序。
      // 序号显示、校验及提交复用此结果，保证参考图顺序一致。
      const keys = main.referenceKeys || [];
      return this.selectedPublicReferences.filter((item) => keys.indexOf(item.refKey) !== -1);
    },
    handleExceedSize() {
      this.$Message.error("文件大小超出限制，最大为10M");
    },
    handleUploadError() {
      this.$Message.error("系统繁忙，请重新尝试");
    },
    handleFormatError() {
      this.$Message.error("文件格式有误，仅支持 jpg / png / gif");
    },
    // 实际上传及回填由 uploadFiles 处理，此处保留上传组件的回调接口。
    handleUploadSuccess() { },
    beforeUpload(file, target) {
      const refName = target === "public" ? "publicUploadRef" : "mainUploadRef";
      const uploadRef = this.$refs[refName];
      const input = uploadRef && uploadRef.$refs && uploadRef.$refs.input;
      const selectedFiles = input ? input.files : [];
      const currentIndex = Array.prototype.indexOf.call(selectedFiles, file);
      const currentCount = (target === "public" ? this.publicReferences.length : this.mainImages.length) + this.uploadQueues[target].length + this.uploadingCounts[target];
      const maxLength = target === "public" ? 5 : this.uploadMaxLength;

      // 整批超限只提示一次，仍逐张接收剩余名额内的文件。
      if (currentIndex === 0 && currentCount + selectedFiles.length > maxLength) {
        this.$Message.error(`图片最多${maxLength}张，超出部分已忽略`);
      }
      if (currentCount + 1 > maxLength) return false;

      const form = new FormData();
      form.append("files", file);
      this.uploadQueues[target].push(form);
      // 合并短时间内的逐文件回调，再统一发起请求；返回 false 阻止组件自动上传。
      if (this.uploadTimers[target]) clearTimeout(this.uploadTimers[target]);
      this.uploadTimers[target] = setTimeout(() => this.uploadFiles(target), 300);
      return false;
    },
    uploadFiles(target) {
      const queue = this.uploadQueues[target].slice();
      if (queue.length === 0) return;
      const version = this.uploadVersion;
      this.uploadingCounts[target] += queue.length;
      this.$set(this.uploadQueues, target, []);
      let uploadFailed = false;
      // Promise.all 按队列顺序返回结果，图片排列不受请求完成先后影响。
      Promise.all(
        queue.map((form) => {
          return this.axios.post(`${this.uploadFilesUrl}&random=${Math.random().toString().substring(2)}`, form).catch(() => {
            // 等待本批全部请求结束再释放名额，并保留其他成功上传的图片。
            uploadFailed = true;
            return null;
          });
        }),
      )
        .then((list) => {
          if (version !== this.uploadVersion) return;
          const paths = [];
          list.forEach((res) => {
            if (res && res.data && Array.isArray(res.data.datas)) {
              res.data.datas.forEach((path) => paths.push(path));
            }
          });
          if (target === "public") this.addUploadedPublicImages(paths);
          else this.addUploadedMainImages(paths);
          if (uploadFailed) this.$Message.error("部分图片上传失败，请重试");
        })
        .catch(() => {
          if (version === this.uploadVersion) this.$Message.error("上传失败，请重试");
        })
        .then(() => {
          if (version === this.uploadVersion) this.uploadingCounts[target] -= queue.length;
        });
    },
    addUploadedPublicImages(paths) {
      // 上传的公共图片默认选中，去重及限量后只将实际新增图片同步到各任务。
      const oldKeys = this.publicReferences.map((item) => this.getImageKey(item));
      const newImages = paths.map((path) => {
        return this.withReferenceKey({
          path,
          sourceType: "upload",
          selected: true,
        });
      });
      this.publicReferences = this.uniqueImages(this.publicReferences.concat(newImages)).slice(0, 5);
      const addedKeys = this.publicReferences.map((item) => this.getImageKey(item)).filter((key) => oldKeys.indexOf(key) === -1);
      this.mainImages.forEach((main) => {
        const keys = (main.referenceKeys || []).slice();
        addedKeys.forEach((key) => {
          if (keys.indexOf(key) === -1) keys.push(key);
        });
        this.$set(main, "referenceKeys", keys);
      });
    },
    addUploadedMainImages(paths) {
      const newImages = paths.map((path) => {
        return this.buildMainImage(
          {
            path,
            sourceType: "upload",
          },
          null,
        );
      });
      this.mainImages = this.mainImages.concat(newImages);
    },
    validate() {
      // 先检查整体生成条件，再逐一检查 AI 主图的有效提示词与参考图。
      if (this.mainImages.length === 0) {
        this.$Message.error("请至少选择一张商品主图");
        return false;
      }
      if (this.aiMainImages.length === 0) {
        this.$Message.error("商品主图中至少需要一张选择“AI生图”模式");
        return false;
      }
      if (this.selectedPublicReferences.length === 0) {
        this.$Message.error("公共参考图片至少需要选择一张");
        return false;
      }

      const invalidTaskIndex = this.mainImages.findIndex((main) => {
        if (this.getGenMode(main) !== "ai") return false;
        if (!this.getEffectivePrompt(main)) return true;
        return this.getTaskReferences(main).length === 0;
      });
      if (invalidTaskIndex !== -1) {
        const main = this.mainImages[invalidTaskIndex];
        if (!this.getEffectivePrompt(main)) {
          this.$Message.error(`任务${invalidTaskIndex + 1}请输入任务提示词或全局通用提示词`);
        } else {
          this.$Message.error(`任务${invalidTaskIndex + 1}至少需要选择一张参考图`);
        }
        return false;
      }
      return true;
    },
    getFormData() {
      // const publicReferences = this.selectedPublicReferences;
      return {
        prompt: (this.globalPrompt || "").trim(),
        // referenceImageList: publicReferences.map((item, index) => {
        //   return Object.assign({ sortNo: index + 1 }, this.imagePayload(item));
        // }),
        // 所有主图按当前排列提交，包括保留原图；仅 AI 主图附带提示词和参考图。
        productAiGenerateImageDetailQOList: this.mainImages.map((main, index) => {
          const data = Object.assign(
            {
              sortNo: index + 1,
              generateType: this.getGenMode(main) === "ai" ? 1 : 2,
            },
            this.imagePayload(main),
          );
          if (this.getGenMode(main) === "ai") {
            data.prompt = this.getEffectivePrompt(main);
            // 主图占图1，任务选中的参考图从图2起连续编号。
            data.referenceImageList = this.getTaskReferences(main).map((item, refIndex) => {
              return Object.assign({ sortNo: refIndex + 2 }, this.imagePayload(item));
            });
          }
          return data;
        }),
      };
    },
  },
};
</script>

<style scoped lang="less">
.ai-gen-section {
  margin-bottom: 20px;
}

.ai-gen-section-title {
  margin-bottom: 10px;
  color: #17233d;
  font-size: 14px;
  font-weight: 600;

  .ai-gen-hint {
    margin-left: 4px;
    color: #999;
    font-size: 12px;
    font-weight: normal;
  }
}

.ai-gen-section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  // margin-bottom: 10px;
}

.ai-gen-ref-summary {
  flex-shrink: 0;
  margin-left: 8px;
  color: #2d8cf0;
  font-size: 12px;
  white-space: nowrap;
}

.ai-fusion-ref-box,
.ai-fusion-main-box {
  position: relative;
  min-height: 184px;
  padding: 16px;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  background: #fff;
}

.ai-fusion-ref-list,
.ai-fusion-main-list {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
}

.ai-fusion-ref-item,
.ai-fusion-main-item {
  width: 120px;
  overflow: hidden;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  background: #fff;
}

.ai-fusion-ref-item {
  cursor: pointer;

  &.selected {
    border-color: #2d8cf0;
    box-shadow: 0 0 0 2px rgba(45, 140, 240, 0.16);
  }
}

.ai-fusion-main-item {
  cursor: move;
}

.ai-fusion-ref-thumb,
.ai-fusion-main-thumb {
  position: relative;
  height: 120px;
  overflow: hidden;
  background: #f8f8f9;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.ai-fusion-ref-thumb {
  height: 152px;
}

.ai-fusion-image-badges {
  position: absolute;
  top: 4px;
  right: 4px;
  left: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  pointer-events: none;
}

.ai-fusion-image-badge {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 8px;
  color: #fff;
  font-size: 11px;
  line-height: 16px;
}

.ai-fusion-image-badge.is-order {
  min-width: 18px;
  background: rgba(23, 35, 61, 0.65);
  text-align: center;
}

.ai-fusion-image-badge.is-source {
  background: rgba(144, 147, 153, 0.9);
}

.ai-fusion-image-badge.is-lang {
  background: #19be6b;
}

.ai-fusion-image-badge.is-set {
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-fusion-ref-check {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 19px;
  height: 19px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #2d8cf0;
  color: #fff;
  font-size: 11px;
}

.ai-fusion-image-cover {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;

  i {
    padding: 4px 8px;
    font-size: 26px;
    pointer-events: auto;
    cursor: pointer;
  }
}

.ai-fusion-ref-thumb:hover .ai-fusion-image-cover,
.ai-fusion-main-thumb:hover .ai-fusion-image-cover {
  opacity: 1;
}

.ai-fusion-mode-btns {
  display: flex;
  border-top: 1px solid #dcdee2;

  button {
    flex: 1;
    padding: 6px 0;
    border: 0;
    background: #f8f8f9;
    color: #515a6e;
    font-size: 12px;
    cursor: pointer;

    &+button {
      border-left: 1px solid #dcdee2;
    }

    &.active {
      background: #2d8cf0;
      color: #fff;
    }
  }
}

.ai-gen-add-item {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 152px;
  border: 1px dashed #c0c4cc;
  border-radius: 4px;
  color: #666;
  cursor: pointer;

  &:hover {
    border-color: #2d8cf0;
    color: #2d8cf0;
  }

  span {
    margin-top: 5px;
    color: #999;
    font-size: 11px;
  }
}

// .ai-fusion-task-head {
//   padding-bottom: 8px;
//   border-bottom: 1px solid #f1f5f9;
// }

.ai-fusion-task-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ai-fusion-empty {
  padding: 22px;
  color: #999;
  text-align: center;
}

.ai-fusion-task {
  position: relative;
  padding: 18px 12px 12px;
  border: 1px solid #e2e8ee;
  border-radius: 5px;
  background: #fbfcfe;
}

.ai-fusion-task-title {
  position: absolute;
  top: -8px;
  left: 10px;
  padding: 0 5px;
  background: #fbfcfe;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
}

.ai-fusion-task-images {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  // min-height: 150px;
}

.ai-fusion-task-image,
.ai-fusion-task-reference {
  flex-shrink: 0;
  color: #64748b;
  font-size: 11px;
  text-align: center;
}

.ai-fusion-task-thumb {
  width: 96px;
  height: 96px;
  overflow: hidden;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  background: #f8f8f9;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.ai-fusion-plus {
  padding-top: 40px;
  color: #94a3b8;
  font-size: 20px;
}

.ai-fusion-task-references {
  flex: 1;
  min-width: 0;
}

.ai-fusion-reference-label {
  display: block;
  margin-bottom: 5px;
  color: #64748b;
  font-size: 12px;
  text-align: left;
}

.ai-fusion-task-reference-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ai-fusion-task-reference button {
  position: relative;
  display: block;
  width: 72px;
  height: 72px;
  padding: 0;
  overflow: hidden;
  border: 2px solid #cbd5e1;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;

  &.selected {
    border-color: #2d8cf0;
  }

  span {
    position: absolute;
    top: 3px;
    right: 3px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    background: #2d8cf0;
    color: #fff;
    font-size: 11px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.ai-fusion-keep-note {
  padding-top: 40px;
  color: #64748b;
  font-size: 12px;
}

.ai-fusion-task-prompt {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #e2e8ee;
}

.ai-fusion-task-prompt-label {
  margin-bottom: 5px;
  color: #64748b;
  font-size: 12px;

  span {
    color: #ed4014;
  }
}
</style>

<style lang="less">
.ai-generate-modal .ai-gen-upload .ivu-upload {
  display: inline-block;
}
</style>
